import dayjs from 'dayjs'

/** Tooltip text for constraint enforcement level badges */
export const HARD_CONSTRAINT_TOOLTIP =
  'Required — must be met to generate schedule'
export const SOFT_CONSTRAINT_TOOLTIP = 'Preference — scheduler tries to satisfy'

/**
 * Constraint type codes managed in the "Blackout & Bye Weeks" section.
 * These are excluded from the main Constraints section tables.
 */
export const BLACKOUT_BYE_CONSTRAINT_CODES: string[] = []

/**
 * Constraint tier definitions for UI separation.
 * Each constraint type belongs to exactly one tier, which determines
 * where it appears in the wizard UI.
 */
export type ConstraintTier = 'team' | 'division' | 'global'

export const TEAM_CONSTRAINT_CODES = [
  'team_bye_period',
  'preferred_day',
  'restricted_day',
  'pair_matchup_count',
] as const

/**
 * Constraint codes that belong to the team tier (so they remain in
 * `TEAM_CONSTRAINT_CODES` for tier classification) but should NOT
 * appear in the per-team constraint sub-table or the per-team add
 * picker. Managed via dedicated UI surfaces:
 *   - pair_matchup_count → MatchupMatrixModal (Matchup Config)
 *
 * Kept as a separate list (not by mutating TEAM_CONSTRAINT_CODES) to
 * avoid cascading effects through useConstraintTypes / CONSTRAINT_TIER_MAP.
 */
export const TEAM_HIDDEN_FROM_PER_TEAM_VIEW_CODES: readonly string[] = [
  'pair_matchup_count',
]

export const DIVISION_CONSTRAINT_CODES = [
  'min_rest_days',
  'no_back_to_back',
  'max_games_per_team_per_day',
  'games_per_team_per_week',
  'max_consecutive_late_games',
] as const

export const GLOBAL_ONLY_CONSTRAINT_CODES = [
  'venue_blackout',
  'venue_capacity',
  'time_window',
  'bye_distribution',
  'home_away_balance',
  'preferred_time',
  'facility_distribution',
] as const

/** Union of all tier arrays — used as the full allowlist of supported codes */
export const ALL_CONSTRAINT_CODES: readonly string[] = [
  ...GLOBAL_ONLY_CONSTRAINT_CODES,
  ...DIVISION_CONSTRAINT_CODES,
  ...TEAM_CONSTRAINT_CODES,
]

export const CONSTRAINT_TIER_MAP: Record<string, ConstraintTier> = {
  team_bye_period: 'team',
  preferred_day: 'team',
  restricted_day: 'team',
  pair_matchup_count: 'team',
  min_rest_days: 'division',
  no_back_to_back: 'division',
  max_games_per_team_per_day: 'division',
  games_per_team_per_week: 'division',
  max_consecutive_late_games: 'division',
  venue_blackout: 'global',
  venue_capacity: 'global',
  time_window: 'global',
  bye_distribution: 'global',
  home_away_balance: 'global',
  preferred_time: 'global',
  facility_distribution: 'global',
}

/** Short day names indexed by day-of-week (0 = Sun) */
const DAY_SHORT_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

/**
 * Format a single day value as a short label. Tolerates both numeric
 * day-of-week values (0-6, the UI's contract) and legacy string day
 * names (e.g. 'Monday').
 */
const formatDayLabel = (d: string | number): string =>
  typeof d === 'number' ? DAY_SHORT_NAMES[d] ?? String(d) : d.slice(0, 3)

/**
 * Client-side display name overrides for constraint type codes.
 * Falls through to the API name when no override exists.
 *
 * Empty since 2026-07-29: `scheduler_constraint_types.name` is now the single
 * source of truth for constraint copy (James's naming pass), so the two former
 * overrides — `venue_blackout` and `pair_matchup_count` — were folded into the
 * DB and removed. Kept as a hook for future code-only renames that must not
 * touch the wire type code.
 */
const CONSTRAINT_NAME_OVERRIDES: Record<string, string> = {}

export const getConstraintDisplayName = (
  code: string,
  apiName: string
): string => CONSTRAINT_NAME_OVERRIDES[code] ?? apiName

/**
 * Map API category to display category label.
 *
 * The product set is Availability / Time / Distribution. `fairness` ('Game')
 * and `preference` ('Dates') are legacy — no seeded type uses them since the
 * 2026-07-29 re-categorisation, but they stay mapped so any custom or
 * not-yet-migrated row still renders a sensible badge instead of a raw code.
 */
export const mapCategoryToDisplay = (category: string): string => {
  switch (category) {
    case 'venue':
    case 'availability':
      return 'Availability'
    case 'time':
      return 'Time'
    case 'distribution':
      return 'Distribution'
    case 'fairness':
      return 'Game'
    case 'preference':
      return 'Dates'
    default:
      return category
  }
}

/**
 * Format a human-readable summary of constraint parameters.
 * Returns null when no meaningful summary can be produced.
 */
export const formatConstraintParameterSummary = (
  code: string,
  parameters: Record<string, unknown>,
  // Optional uid -> display-name map (venues + facilities). Callers with the
  // flight's venue tree in scope (ConstraintsSection) pass it so entity-
  // referencing summaries (facility_distribution percentage targets) can
  // name their targets; without it those summaries fall back to
  // Location/Surface level labels.
  entityNames?: Record<string, string>
): string | null => {
  if (!parameters || Object.keys(parameters).length === 0) {
    return null
  }

  switch (code) {
    case 'venue_blackout': {
      const dates = parameters.dates as string[] | undefined
      if (!dates || dates.length === 0) {
        return null
      }
      const venueUids = parameters.venue_uids as string[] | undefined
      const facilityUids = parameters.facility_uids as string[] | undefined
      const scopeCount = (venueUids?.length || 0) + (facilityUids?.length || 0)
      let datePart: string
      if (dates.length <= 3) {
        datePart = dates.map((d) => dayjs(d).format('MMM D')).join(', ')
      } else {
        datePart = `${dates.length} dates blocked`
      }
      if (scopeCount > 0) {
        datePart += ` · ${scopeCount} venue${scopeCount !== 1 ? 's' : ''}`
      }
      return datePart
    }

    case 'venue_capacity': {
      const max = parameters.max_concurrent_games as number | undefined
      if (max == null) {
        return null
      }
      const facilities = parameters.facility_uids as string[] | undefined
      let text = `Max ${max} concurrent game${max !== 1 ? 's' : ''}`
      if (facilities && facilities.length > 0) {
        text += ` (${facilities.length} facilit${
          facilities.length !== 1 ? 'ies' : 'y'
        })`
      }
      return text
    }

    case 'no_back_to_back': {
      const mins = parameters.min_minutes_between as number | undefined
      if (mins == null) {
        return null
      }
      if (mins >= 60 && mins % 60 === 0) {
        const hrs = mins / 60
        return `Min ${hrs} hour${hrs !== 1 ? 's' : ''} between games`
      }
      if (mins >= 60) {
        const hrs = Math.floor(mins / 60)
        const rem = mins % 60
        return `Min ${hrs}h ${rem}m between games`
      }
      return `Min ${mins} min between games`
    }

    case 'min_rest_days': {
      const minDays = parameters.min_days as number | undefined
      if (minDays == null) {
        return null
      }
      return `Min ${minDays} rest day${minDays !== 1 ? 's' : ''}`
    }

    case 'team_bye_period': {
      const start = parameters.start_date as string | undefined
      const end = parameters.end_date as string | undefined
      if (!start || !end) {
        return null
      }
      const s = dayjs(start)
      const e = dayjs(end)
      if (s.year() === e.year()) {
        return `${s.format('MMM D')} – ${e.format('MMM D, YYYY')}`
      }
      return `${s.format('MMM D, YYYY')} – ${e.format('MMM D, YYYY')}`
    }

    case 'preferred_day':
    case 'restricted_day': {
      const days = parameters.days as (string | number)[] | undefined
      if (!days || days.length === 0) {
        return null
      }
      const formatted = days.map(formatDayLabel).join(', ')
      return code === 'restricted_day' ? `Avoid: ${formatted}` : formatted
    }

    case 'max_consecutive_late_games': {
      const n = parameters.max_consecutive as number | undefined
      const threshold = parameters.late_time_threshold as string | undefined
      if (n == null || !threshold) {
        return null
      }
      return `Max ${n} consecutive late game${
        n !== 1 ? 's' : ''
      } (after ${threshold})`
    }

    case 'max_games_per_team_per_day': {
      const maxGames = parameters.max_games as number | undefined
      if (maxGames == null) {
        return null
      }
      return `Max ${maxGames} game${maxGames !== 1 ? 's' : ''}/day`
    }

    case 'games_per_team_per_week': {
      const gpw = parameters.games_per_week as number | undefined
      if (gpw == null) {
        return null
      }
      return `${gpw} game${gpw !== 1 ? 's' : ''}/week`
    }

    case 'pair_matchup_count': {
      const count = parameters.count as number | undefined
      if (count == null) {
        return null
      }
      return `${count} game${count !== 1 ? 's' : ''} between paired teams`
    }

    case 'time_window': {
      const startTime = parameters.start_time as string | undefined
      const endTime = parameters.end_time as string | undefined
      if (!startTime || !endTime) {
        return null
      }
      const days = parameters.days as (string | number)[] | undefined
      let text = `${startTime} – ${endTime}`
      if (days && days.length > 0) {
        text += ` (${days.map(formatDayLabel).join(', ')})`
      }
      return text
    }

    case 'bye_distribution': {
      const max = parameters.max_consecutive_byes as number | undefined
      if (max == null) {
        return null
      }
      return `Max ${max} consecutive bye${max !== 1 ? 's' : ''}`
    }

    case 'home_away_balance': {
      const pct = parameters.target_home_percentage as number | undefined
      if (pct == null) {
        return null
      }
      return `Target ${pct}% home games`
    }

    case 'preferred_time': {
      const startTime = parameters.start_time as string | undefined
      const endTime = parameters.end_time as string | undefined
      if (!startTime || !endTime) {
        return null
      }
      return `Prefer ${startTime} – ${endTime}`
    }

    case 'facility_distribution': {
      // One type, two param families (SN-8069 merge): spread rows summarize
      // their max_deviation, percentage rows their targets, both-family rows
      // join the two with ' · '.
      const parts: string[] = []
      const maxDeviation = parameters.max_deviation as number | undefined
      if (maxDeviation != null) {
        parts.push(
          `Max ${maxDeviation} game${
            maxDeviation !== 1 ? 's' : ''
          } difference between surfaces per team`
        )
      }
      const targets = parameters.targets as
        | { facility_uid?: string; venue_uid?: string; percent?: number }[]
        | undefined
      if (targets && targets.length > 0) {
        // "60% Planet Ice - Rink 1 · 40% Coquitlam (±1)" when the caller
        // supplies entity names; Location/Surface level labels otherwise.
        const label = (t: {
          facility_uid?: string
          venue_uid?: string
        }): string => {
          const uid = t.venue_uid ?? t.facility_uid
          if (uid && entityNames?.[uid]) {
            return entityNames[uid]
          }
          return t.venue_uid ? 'Location' : 'Surface'
        }
        const split = targets
          .map((t) => `${t.percent ?? 0}% ${label(t)}`)
          .join(' · ')
        const tolerance = (parameters.tolerance as number | undefined) ?? 1
        parts.push(`${split} (±${tolerance})`)
      }
      return parts.length > 0 ? parts.join(' · ') : null
    }

    default:
      return null
  }
}
